import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChevronDown, ActivitySquare } from "lucide-react";
import clsx from "clsx";

const EnginePowerGraph = ({ enginePower = [] }) => {
  const [isOpen, setIsOpen] = useState(true);

  // enginePower format: [power, speed]
  // Highcharts needs [speed, power]

  let chartData = (enginePower ?? [])
    .filter(item => item[0] > 0 && item[1] > 0)
    .map(item => [item[1], item[0]]);

  // 👇 Ensure chart starts from zero
  if (chartData.length) {
    chartData = [[0, 0], ...chartData];
  }


  // Extract speeds only (ignore dummy values)
  const speeds = chartData.map(d => d[0]);
  const minSpeed = Math.min(...speeds);
  const maxSpeed = Math.max(...speeds);

  // Define eco & high-speed ranges from data
  const ecoRange = {
    min: minSpeed,
    max: minSpeed + (maxSpeed - minSpeed) * 0.3, // lower 30% of speed range
  };
  const highSpeedRange = {
    min: minSpeed + (maxSpeed - minSpeed) * 0.6, // top 40% of speed range
    max: maxSpeed,
  };

  const options = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
    },
    title: {
      text: null
    },
    xAxis: {
      title: { text: "RPM" },
      labels: { style: { color: "#0a2540" } },
      gridLineColor: "#e5e7eb"
    },
    yAxis: {
      title: { text: "Power (kW)" },
      labels: { style: { color: "#0a2540" } },
      gridLineColor: "#e5e7eb",
    },
    tooltip: {
      formatter: function () {
        return `<b>RPM:</b> ${this.x}<br/><b>Power:</b> ${this.y} kW`;
      },
      backgroundColor: "#fff",
      borderColor: "#d1d5db",
      borderRadius: 8,
      style: { color: "#0a2540" },
    },
    series: [
      {
        name: "Engine Power",
        type: "line",
        data: chartData,
        color: "#1a5276",
        lineWidth: 3,
        marker: {
          enabled: true,
          radius: 4,
          symbol: "circle",
        },
      },
    ],
    credits: { enabled: false },
    legend: { enabled: false },
  };

  return (
    <div className="w-full rounded-md bg-white overflow-hidden shadow-xl backdrop-blur-md transition-all duration-300">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-br from-[#1a325d] to-[#12213f] w-full flex items-center justify-between px-4 py-3 text-left focus:outline-none group"
      >
        <div className="flex items-center gap-3">
          <div className="text-blue-400 transition-all duration-300">
            <ActivitySquare size={20} strokeWidth={2} />
          </div>
          <h3 className="text-[15px] font-semibold text-white tracking-wide">
            Main Engine Power
          </h3>
        </div>

        <ChevronDown
          className={clsx(
            "w-5 h-5 text-white transition-transform duration-300",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Collapsible Content */}
      <div
        className={clsx(
          "transition-all duration-500 ease-in-out overflow-hidden",
          isOpen ? "max-h-[1000px] py-4 px-4" : "max-h-0 py-0 px-4"
        )}
      >
        {isOpen && (
          <HighchartsReact highcharts={Highcharts} options={options} />
        )}
      </div>
    </div>
  );
};

export default EnginePowerGraph;
